import React, {
  useState,
  useMemo,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import {
  EventApi,
  DateSelectArg,
  EventClickArg,
  EventContentArg,
  EventDropArg,
  DatesSetArg,
} from "@fullcalendar/core";
import frLocale from "@fullcalendar/core/locales/fr";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import multiMonthPlugin from "@fullcalendar/multimonth";
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction";
import "./events.css";
import useList from "hooks/useList";
import type { Event } from "types";
import type { EventInput } from "@fullcalendar/core";
import Select from "common/components/Select/Select";
import CrudEdit from "common/components/Crud/CrudEdit";
import { Sidebar } from "primereact/sidebar";
import useItem from "hooks/useItem";
import AlertsContext from "providers/AlertProvider";
import { useTranslation } from "react-i18next";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";
import EventForm from "./EventForm";

// Wrapper pour créer un événement avec des dates pré-remplies
function EventFormCreateWrapper({
  initialValues: customInitialValues,
  onSubmitSuccess,
  onBack,
}: {
  initialValues: Partial<Event>;
  onSubmitSuccess?: () => void;
  onBack?: () => void;
}) {
  const { t } = useTranslation();
  const { setAlert } = useContext(AlertsContext);
  const { saveItem, error } = useItem("events", null);

  useEffect(() => {
    if (error) {
      setAlert(t("common.error"), "danger");
    }
  }, [error, setAlert, t]);

  const onSubmit = useCallback(
    async (data: any) => {
      try {
        const res = await saveItem(data, false);
        if (!error) {
          setAlert(t("common.saved"), "success");
        }
        onSubmitSuccess?.();
        onBack?.();
        return res;
      } catch (e: any) {
        console.error(e);
        return e;
      }
    },
    [saveItem, onSubmitSuccess, onBack, error, setAlert, t],
  );

  return (
    <EventForm
      onSubmit={onSubmit}
      onCancel={onBack}
      item={customInitialValues}
    />
  );
}

// Rendu du contenu d'un événement dans le calendrier
function EventContent({
  eventContent,
  showLocation,
}: {
  eventContent: EventContentArg;
  showLocation: boolean;
}) {
  const view = eventContent.view.type;
  const isListView =
    view === "listWeek" ||
    view === "listDay" ||
    view === "listMonth" ||
    view === "listYear";
  const location = eventContent.event.extendedProps?.location as
    | string
    | undefined;

  if (isListView) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          width: "100%",
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{ fontSize: "14px", fontWeight: "600", marginBottom: "4px" }}
          >
            {eventContent.event.title}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "#666",
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            {eventContent.timeText && <span>{eventContent.timeText}</span>}
            {showLocation && location && <span>• {location}</span>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        fontSize: "12px",
        fontWeight: "500",
        padding: "4px 6px",
        lineHeight: "1.3",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        overflow: "hidden",
      }}
    >
      {eventContent.timeText && (
        <div style={{ fontSize: "11px", opacity: 0.9, fontWeight: "500" }}>
          {eventContent.timeText}
        </div>
      )}
      <div style={{ fontSize: "13px", fontWeight: "700" }}>
        {eventContent.event.title}
      </div>
      {showLocation && location && (
        <div style={{ fontSize: "11px", opacity: 0.85 }}>📍 {location}</div>
      )}
    </div>
  );
}

type RangeType = "day" | "week" | "month" | "year";
type ViewMode = "calendar" | "planning";

type CalendarPrefs = {
  weekendsVisible: boolean;
  slotDuration: string;
  showLocation: boolean;
};

type CalendarUiState = {
  rangeType: RangeType;
  viewMode: ViewMode;
  currentDate: string | null;
};

const PREFS_KEY = "events_calendar_prefs";
const UI_KEY = "events_calendar_ui";

const DEFAULT_PREFS: CalendarPrefs = {
  weekendsVisible: true,
  slotDuration: "02:00:00",
  showLocation: true,
};

const DEFAULT_UI: CalendarUiState = {
  rangeType: "week",
  viewMode: "calendar",
  currentDate: null,
};

function readPrefs(): CalendarPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<CalendarPrefs>;
    const slotDuration =
      parsed.slotDuration === "01:00:00" ||
      parsed.slotDuration === "02:00:00" ||
      parsed.slotDuration === "04:00:00"
        ? parsed.slotDuration
        : DEFAULT_PREFS.slotDuration;
    return {
      weekendsVisible:
        typeof parsed.weekendsVisible === "boolean"
          ? parsed.weekendsVisible
          : DEFAULT_PREFS.weekendsVisible,
      slotDuration,
      showLocation:
        typeof parsed.showLocation === "boolean"
          ? parsed.showLocation
          : DEFAULT_PREFS.showLocation,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

function writePrefs(prefs: CalendarPrefs) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
}

function readUi(): CalendarUiState {
  try {
    const raw = localStorage.getItem(UI_KEY);
    if (!raw) return DEFAULT_UI;
    const parsed = JSON.parse(raw) as Partial<CalendarUiState>;
    const rangeType =
      parsed.rangeType === "day" ||
      parsed.rangeType === "week" ||
      parsed.rangeType === "month" ||
      parsed.rangeType === "year"
        ? parsed.rangeType
        : DEFAULT_UI.rangeType;
    const viewMode =
      parsed.viewMode === "calendar" || parsed.viewMode === "planning"
        ? parsed.viewMode
        : DEFAULT_UI.viewMode;
    return {
      rangeType,
      viewMode,
      currentDate:
        typeof parsed.currentDate === "string" ? parsed.currentDate : null,
    };
  } catch {
    return DEFAULT_UI;
  }
}

function writeUi(ui: CalendarUiState) {
  try {
    localStorage.setItem(UI_KEY, JSON.stringify(ui));
  } catch {
    /* ignore */
  }
}

function getRangeTypeFromView(viewType: string): RangeType {
  if (viewType === "timeGridDay" || viewType === "listDay") return "day";
  if (viewType === "timeGridWeek" || viewType === "listWeek") return "week";
  if (viewType === "dayGridMonth" || viewType === "listMonth") return "month";
  return "year";
}

function getViewForRange(rangeType: RangeType, mode: ViewMode): string {
  if (mode === "planning") {
    if (rangeType === "day") return "listDay";
    if (rangeType === "week") return "listWeek";
    if (rangeType === "month") return "listMonth";
    return "listYear";
  }
  if (rangeType === "day") return "timeGridDay";
  if (rangeType === "week") return "timeGridWeek";
  if (rangeType === "month") return "dayGridMonth";
  return "multiMonthYear";
}

function getCalendarRangeFilters(
  rangeType: RangeType,
  currentDate: string | null,
) {
  const baseDate = currentDate ? new Date(currentDate) : new Date();
  const start = new Date(baseDate);
  const end = new Date(baseDate);

  if (rangeType === "day") {
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
  } else if (rangeType === "week") {
    const daysFromMonday = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - daysFromMonday);
    start.setHours(0, 0, 0, 0);
    end.setTime(start.getTime());
    end.setDate(end.getDate() + 7);
  } else if (rangeType === "month") {
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    end.setFullYear(start.getFullYear(), start.getMonth() + 1, 1);
    end.setHours(0, 0, 0, 0);
  } else {
    start.setFullYear(start.getFullYear(), 0, 1);
    start.setHours(0, 0, 0, 0);
    end.setFullYear(start.getFullYear() + 1, 0, 1);
    end.setHours(0, 0, 0, 0);
  }

  return { startDate: start.toISOString(), endDate: end.toISOString() };
}

function Events() {
  const calendarRef = useRef<FullCalendar | null>(null);
  const miniCalendarRef = useRef<FullCalendar | null>(null);
  const targetDateRef = useRef<Date | null>(null);
  const { t } = useTranslation();

  const initialUi = readUi();
  const initialRangeFilters = useMemo(
    () => getCalendarRangeFilters(initialUi.rangeType, initialUi.currentDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const {
    items: calendarEvents,
    isFetching: isFetchingEvents,
    fetchItems: fetchEvents,
    updateListItem: updateEventInList,
    setFilter: setEventFilter,
  } = useList<Event>("events/data/calendar", {
    defaultFilters: { ...initialRangeFilters },
    defaultItemsPerPage: 1000,
  });

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [newEventInitialValues, setNewEventInitialValues] = useState<any>(null);
  const [sidebarMode, setSidebarMode] = useState<"edit" | "create" | null>(
    null,
  );
  const [rangeType, setRangeType] = useState<RangeType>(initialUi.rangeType);
  const [viewMode, setViewMode] = useState<ViewMode>(initialUi.viewMode);
  const [calendarTitle, setCalendarTitle] = useState<string>("");
  const [isSettingsSidebarOpen, setIsSettingsSidebarOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState<string | null>(
    initialUi.currentDate,
  );

  const [prefs, setPrefs] = useState<CalendarPrefs>(() => readPrefs());

  const savePrefs = (next: CalendarPrefs) => {
    writePrefs(next);
    setPrefs(next);
  };

  const events = useMemo<EventInput[]>(() => {
    if (!calendarEvents || calendarEvents.length === 0) return [];

    return calendarEvents
      .filter((event) => event.startDateTime && event.endDateTime)
      .map((event) => {
        const color = event.eventType?.color ?? "#777777";
        return {
          id: `event-${event.id}`,
          title: event.name,
          start: event.startDateTime,
          end: event.endDateTime,
          backgroundColor: color,
          borderColor: color,
          extendedProps: {
            eventId: event.id,
            location: event.location,
            description: event.description,
          },
        };
      });
  }, [calendarEvents]);

  const changeCalendarView = useCallback(
    (nextRangeType: RangeType, nextViewMode: ViewMode) => {
      const calendarApi = calendarRef.current?.getApi();
      if (!calendarApi) return;
      calendarApi.changeView(getViewForRange(nextRangeType, nextViewMode));
    },
    [],
  );

  const handleRangeTypeChange = useCallback(
    (value: RangeType) => {
      setRangeType(value);
      changeCalendarView(value, viewMode);
    },
    [changeCalendarView, viewMode],
  );

  const handleViewModeChange = useCallback(
    (value: ViewMode) => {
      setViewMode(value);
      changeCalendarView(rangeType, value);
    },
    [changeCalendarView, rangeType],
  );

  const handleDatesSet = useCallback(
    (args: DatesSetArg) => {
      setCalendarTitle(args.view.title);
      if (args.view.type.startsWith("list")) {
        setViewMode("planning");
      } else {
        setViewMode("calendar");
      }
      setRangeType(getRangeTypeFromView(args.view.type));
      setCurrentDate(args.view.currentStart.toISOString());
      setEventFilter("startDate", args.start.toISOString());
      setEventFilter("endDate", args.end.toISOString());
    },
    [setEventFilter],
  );

  const handleTodayClick = useCallback(() => {
    calendarRef.current?.getApi().today();
  }, []);

  const handlePrevClick = useCallback(() => {
    calendarRef.current?.getApi().prev();
  }, []);

  const handleNextClick = useCallback(() => {
    calendarRef.current?.getApi().next();
  }, []);

  const handleMiniDateClick = useCallback(
    (arg: DateClickArg) => {
      if (!prefs.weekendsVisible) {
        const day = arg.date.getDay();
        if (day === 0 || day === 6) return;
      }
      const calendarApi = calendarRef.current?.getApi();
      if (!calendarApi) return;

      if (viewMode === "calendar" && rangeType === "day") {
        calendarApi.gotoDate(arg.date);
      } else {
        targetDateRef.current = arg.date;
        setViewMode("calendar");
        setRangeType("day");
      }
    },
    [prefs.weekendsVisible, viewMode, rangeType],
  );

  const handleCreateClick = useCallback(() => {
    setSelectedEventId(null);
    setNewEventInitialValues({});
    setSidebarMode("create");
    setIsSidebarOpen(true);
  }, []);

  useEffect(() => {
    changeCalendarView(rangeType, viewMode);
    if (targetDateRef.current) {
      const api = calendarRef.current?.getApi();
      if (api) {
        api.gotoDate(targetDateRef.current);
        targetDateRef.current = null;
      }
    }
  }, [changeCalendarView, rangeType, viewMode]);

  useEffect(() => {
    if (!currentDate) return;
    const miniApi = miniCalendarRef.current?.getApi();
    if (!miniApi) return;
    const nextDate = new Date(currentDate);
    const currentMiniDate = miniApi.getDate();
    if (currentMiniDate.toDateString() !== nextDate.toDateString()) {
      miniApi.gotoDate(nextDate);
    }
  }, [currentDate]);

  useEffect(() => {
    writeUi({ rangeType, viewMode, currentDate });
  }, [rangeType, viewMode, currentDate]);

  const handleDateSelect = (selectInfo: DateSelectArg) => {
    const calendarApi = selectInfo.view.calendar;
    calendarApi.unselect();
    setSelectedEventId(null);
    setNewEventInitialValues({
      startDateTime: new Date(selectInfo.startStr),
      endDateTime: new Date(selectInfo.endStr),
    });
    setSidebarMode("create");
    setIsSidebarOpen(true);
  };

  const handleEventClick = (clickInfo: EventClickArg) => {
    const eventId = clickInfo.event.extendedProps?.eventId;
    if (eventId) {
      setSelectedEventId(eventId);
      setNewEventInitialValues(null);
      setSidebarMode("edit");
      setIsSidebarOpen(true);
    }
  };

  const handleSidebarClose = () => {
    setIsSidebarOpen(false);
    setSelectedEventId(null);
    setNewEventInitialValues(null);
    setSidebarMode(null);
  };

  const handleEventSave = () => {
    fetchEvents();
    handleSidebarClose();
  };

  const { updateItem } = useItem<Event>("events", null);

  const handleEventDrop = useCallback(
    async (dropInfo: EventDropArg) => {
      const eventId = dropInfo.event.extendedProps?.eventId;
      if (!eventId) return;

      try {
        const startDate = new Date(dropInfo.event.start!);
        const endDate = dropInfo.event.end
          ? new Date(dropInfo.event.end)
          : new Date(startDate.getTime() + 2 * 60 * 60 * 1000);

        const updateData = {
          startDateTime: startDate.toISOString(),
          endDateTime: endDate.toISOString(),
        };

        await updateItem(eventId, { data: updateData }, false);
        updateEventInList(eventId, updateData);
      } catch (error) {
        console.error("Erreur lors du déplacement de l'événement:", error);
        dropInfo.revert();
      }
    },
    [updateItem, updateEventInList],
  );

  const handleEventResize = useCallback(
    async (resizeInfo: any) => {
      const eventId = resizeInfo.event.extendedProps?.eventId;
      if (!eventId) return;

      try {
        const startDate = new Date(resizeInfo.event.start!);
        const endDate = resizeInfo.event.end
          ? new Date(resizeInfo.event.end)
          : new Date(startDate.getTime() + 2 * 60 * 60 * 1000);

        const updateData = {
          startDateTime: startDate.toISOString(),
          endDateTime: endDate.toISOString(),
        };

        await updateItem(eventId, { data: updateData }, false);
        updateEventInList(eventId, updateData);
      } catch (error) {
        console.error(
          "Erreur lors du redimensionnement de l'événement:",
          error,
        );
        resizeInfo.revert();
      }
    },
    [updateItem, updateEventInList],
  );

  const handleEvents = (_eventsAPI: EventApi[]) => {
    /* noop */
  };

  return (
    <div className="calendar-container">
      <div className="calendar-sidebar">
        <div className="calendar-sidebar-action">
          <button
            type="button"
            className="calendar-sidebar-create"
            onClick={handleCreateClick}
          >
            {t("events.createEvent")}
          </button>
        </div>
        <div className="calendar-sidebar-section calendar-mini">
          <FullCalendar
            ref={miniCalendarRef}
            plugins={[dayGridPlugin, interactionPlugin]}
            locale={frLocale}
            headerToolbar={{ left: "title", center: "", right: "prev,next" }}
            initialView="dayGridMonth"
            initialDate={currentDate || undefined}
            height="auto"
            contentHeight="auto"
            fixedWeekCount={false}
            showNonCurrentDates
            events={[]}
            dateClick={handleMiniDateClick}
            dayCellClassNames={(info) => {
              const classes: string[] = [];
              if (!prefs.weekendsVisible) {
                const day = info.date.getDay();
                if (day === 0 || day === 6) classes.push("is-weekend-disabled");
              }
              if (currentDate) {
                const selectedDate = new Date(currentDate);
                if (
                  info.date.getFullYear() === selectedDate.getFullYear() &&
                  info.date.getMonth() === selectedDate.getMonth() &&
                  info.date.getDate() === selectedDate.getDate()
                ) {
                  classes.push("is-selected-day");
                }
              }
              return classes;
            }}
          />
        </div>
      </div>

      <div className="calendar-main" style={{ position: "relative" }}>
        <div className="calendar-header">
          <div className="calendar-header-left">
            <button
              type="button"
              className="calendar-header-button"
              onClick={handleTodayClick}
            >
              {t("calendar.today")}
            </button>
            <div className="calendar-header-nav">
              <button
                type="button"
                className="calendar-header-icon-button"
                onClick={handlePrevClick}
                aria-label={t("calendar.previous")}
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              <button
                type="button"
                className="calendar-header-icon-button"
                onClick={handleNextClick}
                aria-label={t("calendar.next")}
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="calendar-header-title">{calendarTitle}</div>
          </div>
          <div className="calendar-header-right">
            <button
              type="button"
              className="calendar-header-icon-button"
              onClick={() => setIsSettingsSidebarOpen(true)}
              aria-label={t("calendar.settings")}
            >
              <Cog6ToothIcon className="w-5 h-5" />
            </button>
            <div className="calendar-header-range">
              <Select
                value={rangeType}
                onChange={(value) => handleRangeTypeChange(value)}
                options={[
                  { value: "day", label: t("calendar.rangeDay") },
                  { value: "week", label: t("calendar.rangeWeek") },
                  { value: "month", label: t("calendar.rangeMonth") },
                  { value: "year", label: t("calendar.rangeYear") },
                ]}
                clearable={false}
              />
            </div>
            <div className="calendar-header-mode">
              <button
                type="button"
                className={`calendar-header-mode-button ${
                  viewMode === "calendar" ? "is-active" : ""
                }`}
                onClick={() => handleViewModeChange("calendar")}
              >
                {t("calendar.viewCalendar")}
              </button>
              <button
                type="button"
                className={`calendar-header-mode-button ${
                  viewMode === "planning" ? "is-active" : ""
                }`}
                onClick={() => handleViewModeChange("planning")}
              >
                {t("calendar.viewPlanning")}
              </button>
            </div>
          </div>
        </div>

        <FullCalendar
          ref={calendarRef}
          plugins={[
            dayGridPlugin,
            timeGridPlugin,
            listPlugin,
            multiMonthPlugin,
            interactionPlugin,
          ]}
          locale={frLocale}
          headerToolbar={false}
          initialDate={currentDate || undefined}
          initialView={getViewForRange(rangeType, viewMode)}
          editable
          selectable
          selectMirror
          weekends={prefs.weekendsVisible}
          events={events}
          select={handleDateSelect}
          eventContent={(info) => (
            <EventContent
              eventContent={info}
              showLocation={prefs.showLocation}
            />
          )}
          eventClick={handleEventClick}
          eventsSet={handleEvents}
          datesSet={handleDatesSet}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          views={{ multiMonthYear: { multiMonthMaxColumns: 1 } }}
        />

        {isFetchingEvents && (
          <div className="calendar-loading-indicator">
            <div
              style={{
                display: "inline-block",
                padding: "8px 16px",
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                color: "white",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            >
              {t("calendar.loading")}
            </div>
          </div>
        )}
      </div>

      {/* Sidebar paramètres */}
      <Sidebar
        visible={isSettingsSidebarOpen}
        onHide={() => setIsSettingsSidebarOpen(false)}
        position="right"
        className="w-full md:w-96"
      >
        <div className="calendar-sidebar-section" style={{ border: "none" }}>
          <h2>{t("calendar.preferencesTitle")}</h2>
          <label>
            <input
              type="checkbox"
              checked={prefs.weekendsVisible}
              onChange={() =>
                savePrefs({ ...prefs, weekendsVisible: !prefs.weekendsVisible })
              }
            />{" "}
            {t("calendar.showWeekends")}
          </label>
        </div>
        <div className="calendar-sidebar-section" style={{ border: "none" }}>
          <h2>{t("calendar.timeScale")}</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {(["01:00:00", "02:00:00", "04:00:00"] as const).map((val, i) => (
              <label key={val}>
                <input
                  type="radio"
                  name="slotDuration"
                  value={val}
                  checked={prefs.slotDuration === val}
                  onChange={() => savePrefs({ ...prefs, slotDuration: val })}
                />{" "}
                {
                  [
                    t("calendar.oneHour"),
                    t("calendar.twoHours"),
                    t("calendar.fourHours"),
                  ][i]
                }
              </label>
            ))}
          </div>
        </div>
        <div className="calendar-sidebar-section" style={{ border: "none" }}>
          <h2>{t("calendar.displayPreferences")}</h2>
          <label>
            <input
              type="checkbox"
              checked={prefs.showLocation}
              onChange={() =>
                savePrefs({ ...prefs, showLocation: !prefs.showLocation })
              }
            />
            {t("calendar.showLocation")}
          </label>
        </div>
      </Sidebar>

      {/* Sidebar création / édition */}
      <Sidebar
        visible={isSidebarOpen}
        onHide={handleSidebarClose}
        position="right"
        className="w-full md:w-2/3 lg:w-1/2"
      >
        {sidebarMode === "edit" && selectedEventId ? (
          <CrudEdit
            collectionName="events"
            id={selectedEventId}
            Form={EventForm}
            onSubmitSuccess={handleEventSave}
            onBack={handleSidebarClose}
            onDelete={handleEventSave}
          />
        ) : sidebarMode === "create" && newEventInitialValues ? (
          <EventFormCreateWrapper
            initialValues={newEventInitialValues}
            onSubmitSuccess={handleEventSave}
            onBack={handleSidebarClose}
          />
        ) : null}
      </Sidebar>
    </div>
  );
}

export default Events;
